import React from "react";
import { Link, useParams } from "@tanstack/react-router";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import NotFound from "@/pages/NotFound";
import { getTag, getPostsByTag, getSectionBySlug, type Post, type PostType } from "@/data/library";
import { LibraryThumb } from "@/components/library/LibraryThumb";
import { TypeTagRail } from "@/components/library/TypeTagRail";
import { TagChips } from "@/components/library/TagChips";

const PostCard = ({ post }: { post: Post }) => (
  <Link to={post.href} className="group flex flex-col">
    <div className="flex items-center justify-between mb-4">
      <time className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
        {post.date}
      </time>
      <TagChips tags={post.tags} />
    </div>
    <div className="mb-6 overflow-hidden rounded-sm">
      <LibraryThumb slug={post.slug} image={post.image} live />
    </div>
    <h2
      className="text-xl md:text-2xl leading-tight tracking-tight mb-3 group-hover:underline underline-offset-4 decoration-1"
      style={{ fontFamily: "var(--font-body)" }}
    >
      {post.title}
    </h2>
    <p className="text-muted-foreground leading-relaxed line-clamp-3">{post.dek}</p>
  </Link>
);

const ListPostItem = ({ post }: { post: Post }) => (
  <Link
    to={post.href}
    className="group grid grid-cols-[100px_1fr] md:grid-cols-[160px_1fr] lg:grid-cols-[180px_1fr] gap-4 md:gap-8 lg:gap-10 py-6 md:py-8 lg:py-10 border-b border-foreground last:border-b-0"
  >
    <div className="overflow-hidden rounded-sm">
      <LibraryThumb slug={post.slug} image={post.image} live />
    </div>
    <div className="flex flex-col justify-center">
      <span className="mb-2 md:mb-3 inline-flex self-start">
        <TagChips tags={post.tags} />
      </span>
      <h2
        className="text-lg md:text-xl lg:text-2xl leading-tight tracking-tight mb-2 md:mb-3 group-hover:underline underline-offset-4 decoration-1"
        style={{ fontFamily: "var(--font-body)" }}
      >
        {post.title}
      </h2>
      <p className="text-sm md:text-base text-muted-foreground leading-relaxed line-clamp-2">
        {post.dek}
      </p>
    </div>
  </Link>
);

const BlogTopic = ({ surfaceSlug, scope }: { surfaceSlug?: string; scope?: PostType }) => {
  // Three uses: /blog|/metrics|/guides "/topic/$tag" pages (tag from the URL,
  // scoped to that type), and the root surfaces /metrics and /guides (full
  // section listing, pinned via prop). The routes' loaders have already
  // validated tag/section existence and non-emptiness.
  const { tag: tagParam = "" } = useParams({ strict: false });
  const surface = surfaceSlug ? getSectionBySlug(surfaceSlug) : undefined;
  const tag = surface ?? getTag(tagParam);

  if (!tag) return <NotFound />;

  const tagged = surface ? surface.posts : getPostsByTag(tag.slug);
  const posts = scope ? tagged.filter((p) => p.href.startsWith(`/${scope}/`)) : tagged;
  // The rail's base: this type's index ("All"), where tags can be switched.
  const railBase = scope ? `/${scope}` : `/${surfaceSlug}`;
  const eyebrow =
    railBase === "/metrics"
      ? "Bigdelta Metrics"
      : railBase === "/guides"
        ? "Bigdelta Guides"
        : "Bigdelta Blog";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main>
        {/* Header mirrors the type index page (same padding + eyebrow) so
            switching tags via the rail doesn't shift the layout. */}
        <header className="pt-40 md:pt-52 px-6 md:px-10">
          <div className="max-w-6xl mx-auto border-b border-border pb-12 md:pb-16">
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                {eyebrow}
              </span>
              <h1
                className="mt-6 text-5xl md:text-6xl leading-[1.05] tracking-tight"
                style={{ fontFamily: "var(--font-body)" }}
              >
                {tag.name}
              </h1>
              <p className="mt-6 text-lg md:text-xl text-muted-foreground leading-relaxed">
                {tag.blurb}
              </p>
            </div>
          </div>
        </header>
        <TypeTagRail base={railBase} current={scope ? tag.slug : undefined} />
        <section className="px-6 md:px-10 py-16 md:py-20">
          <div className="max-w-6xl mx-auto">
            {posts.length === 0 ? (
              <p className="text-muted-foreground">No articles in {tag.name.toLowerCase()} yet.</p>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-12 md:gap-x-8 lg:gap-x-12 md:gap-y-16">
                  {posts.slice(0, 3).map((post) => (
                    <PostCard key={post.slug} post={post} />
                  ))}
                </div>
                {posts.length > 3 && (
                  <div className="mt-16 md:mt-20">
                    {posts.slice(3).map((post) => (
                      <ListPostItem key={post.slug} post={post} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BlogTopic;
